import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useUpdateEventDeliverable,
  type EventDeliverable,
} from "../../hooks/useEventDeliverables";
import {
  deliverableSchema,
  type DeliverableFormData,
} from "../../validators/deliverable.validators";
import { DeliverableFormFields } from "./DeliverableFormFields";

export interface EditDeliverableModalProps {
  deliverable: EventDeliverable | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

export const EditDeliverableModal: React.FC<EditDeliverableModalProps> = ({
  deliverable,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  isOpen,
  onClose,
}) => {
  const isModalOpen = controlledOpen ?? isOpen ?? false;
  const handleOpenChange = (newOpen: boolean) => {
    if (controlledOnOpenChange) {
      controlledOnOpenChange(newOpen);
    }
    if (!newOpen && onClose) {
      onClose();
    }
  };

  const updateDeliverable = useUpdateEventDeliverable();

  const form = useForm<DeliverableFormData>({
    resolver: zodResolver(deliverableSchema),
    defaultValues: {
      influencer_name: "",
      platform: "",
      status: "",
      expected_deliverable: "",
      content_url: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (deliverable) {
      form.reset({
        influencer_name: deliverable.influencer_name,
        platform: deliverable.platform,
        status: deliverable.status,
        expected_deliverable: deliverable.expected_deliverable,
        deadline: deliverable.deadline ? new Date(deliverable.deadline) : undefined,
        content_url: deliverable.content_url || "",
        notes: deliverable.notes || "",
      });
    }
  }, [deliverable, form]);

  const onSubmit = async (data: DeliverableFormData) => {
    if (!deliverable) return;

    try {
      await updateDeliverable.mutateAsync({
        id: deliverable.id,
        influencer_name: data.influencer_name,
        platform: data.platform,
        status: data.status,
        expected_deliverable: data.expected_deliverable,
        deadline: data.deadline ? format(data.deadline, "yyyy-MM-dd") : deliverable.deadline,
        content_url: data.content_url || null,
        notes: data.notes || null,
      });

      handleOpenChange(false);
    } catch (error) {
      console.error("Failed to update deliverable:", error);
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Deliverable</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DeliverableFormFields form={form} />

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={updateDeliverable.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateDeliverable.isPending}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {updateDeliverable.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
